import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import { ProfileCompletenessService } from '../../service/profile-completeness.service';
import {
  CSVService,
  FieldMapperType,
  MultiFieldMapper,
} from '../../service/csv.service';
import { DownloadService } from '../../service/download.service';
import {
  COMPLETENESS_CATEGORIES,
  GymProfileCompleteness,
  NETWORK_FILTER_OPTIONS,
  NetworkFilter,
  Page,
  ProfileCompletenessRequest,
  ProfileCompletenessSummary,
  SortKey,
  STATUS_DISPLAY,
} from '../../model/profile-completeness';

export function sanitizeCsvValue(value: string): string {
  if (value && /^[=+\-@\t\r]/.test(value)) {
    return `'${value}`;
  }
  return value;
}

export const PROFILE_COMPLETENESS_CSV_MAPPINGS: MultiFieldMapper<GymProfileCompleteness> =
  {
    'EGYM Legacy Gym ID': {
      mapperType: FieldMapperType.FUNCTION_EXTRACTOR,
      extractFunction: gym => gym.egymLegacyGymId?.toString() ?? '',
    },
    'EGYM Legacy Gym Chain ID': {
      mapperType: FieldMapperType.FUNCTION_EXTRACTOR,
      extractFunction: gym => gym.egymLegacyGymChainId?.toString() ?? '',
    },
    'Gym Alias': {
      mapperType: FieldMapperType.FUNCTION_EXTRACTOR,
      extractFunction: gym =>
        sanitizeCsvValue(gym.gymName ?? 'Unnamed gym'),
    },
    'Status Photos': {
      mapperType: FieldMapperType.FUNCTION_EXTRACTOR,
      extractFunction: gym =>
        STATUS_DISPLAY[gym.photosStatus]?.label ?? gym.photosStatus,
    },
    'Status Hours': {
      mapperType: FieldMapperType.FUNCTION_EXTRACTOR,
      extractFunction: gym =>
        STATUS_DISPLAY[gym.openingHoursStatus]?.label ?? gym.openingHoursStatus,
    },
    'Status Description': {
      mapperType: FieldMapperType.FUNCTION_EXTRACTOR,
      extractFunction: gym =>
        STATUS_DISPLAY[gym.descriptionStatus]?.label ?? gym.descriptionStatus,
    },
    'Network': {
      mapperType: FieldMapperType.FUNCTION_EXTRACTOR,
      extractFunction: gym =>
        sanitizeCsvValue(gym.network ?? 'Not set'),
    },
  };

type LoadState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly data: T }
  | { readonly status: 'error'; readonly error: HttpErrorResponse };

const LOADING = { status: 'loading' } as const;

const DEFAULT_PAGE_SIZE = 10;

interface Filters {
  readonly searchTerm: string | null;
  readonly networks: readonly NetworkFilter[];
  readonly sorting: SortKey;
}

interface Pagination {
  readonly page: number;
  readonly pageSize: number;
}

@Component({
  selector: 'gymfinder-profile-completeness',
  templateUrl: './profile-completeness.component.html',
  styleUrls: ['./profile-completeness.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileCompletenessComponent {
  private readonly service = inject(ProfileCompletenessService);
  private readonly csvService = inject(CSVService);
  private readonly downloadService = inject(DownloadService);
  private readonly snackBar = inject(MatSnackBar);

  readonly categories = COMPLETENESS_CATEGORIES;
  readonly networkFilterOptions = NETWORK_FILTER_OPTIONS;
  readonly statusDisplay = STATUS_DISPLAY;
  readonly pageSizeOptions = [10, 50, 100];
  readonly displayedColumns = [
    'location',
    'network',
    ...COMPLETENESS_CATEGORIES.map(category => category.key),
    'profile',
  ];

  readonly filters = signal<Filters>({
    searchTerm: null,
    networks: [],
    sorting: 'HOURS',
  });
  readonly pagination = signal<Pagination>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  readonly searchValue = signal('');
  readonly summaryDismissed = signal(false);
  readonly exporting = signal(false);

  private readonly request = computed<ProfileCompletenessRequest>(() => {
    const { searchTerm, networks, sorting } = this.filters();
    const { page, pageSize } = this.pagination();
    return {
      sorting,
      page,
      pageSize,
      ...(searchTerm && { searchTerm }),
      ...(networks.length && { networkFilter: networks }),
    };
  });

  private readonly networks = computed(() => this.filters().networks);

  private readonly pageState = toSignal(
    toObservable(this.request).pipe(
      switchMap(request => this.service.getPage(request).pipe(toLoadState()))
    ),
    { initialValue: LOADING as LoadState<Page<GymProfileCompleteness>> }
  );

  private readonly summaryState = toSignal(
    toObservable(this.networks).pipe(
      distinctUntilChanged(sameNetworks),
      switchMap(networks =>
        this.service.getSummary(networks).pipe(toLoadState())
      )
    ),
    { initialValue: LOADING as LoadState<ProfileCompletenessSummary> }
  );

  readonly gyms = computed<readonly GymProfileCompleteness[]>(() => {
    const state = this.pageState();
    return state.status === 'loaded' ? state.data.items : [];
  });

  readonly total = computed(() => {
    const state = this.pageState();
    return state.status === 'loaded' ? state.data.total : 0;
  });

  readonly loading = computed(() => this.pageState().status === 'loading');

  readonly showEmptyState = computed(() => {
    const state = this.pageState();
    return state.status === 'loaded' && state.data.items.length === 0;
  });

  readonly hasSearchTerm = computed(() => !!this.filters().searchTerm);

  readonly error = computed<HttpErrorResponse | null>(() => {
    const state = this.pageState();
    return state.status === 'error' ? state.error : null;
  });

  readonly summary = computed<ProfileCompletenessSummary | null>(() => {
    const state = this.summaryState();
    return state.status === 'loaded' ? state.data : null;
  });

  readonly summaryVisible = computed(
    () => (this.summary()?.total ?? 0) > 0 && !this.summaryDismissed()
  );

  readonly summaryScope = computed(() => {
    const selected = this.networks();
    if (!selected.length) {
      return 'all networks';
    }
    return this.networkFilterOptions
      .filter(option => selected.includes(option.value))
      .map(option => option.display)
      .join(', ');
  });

  readonly asGym = (row: unknown): GymProfileCompleteness =>
    row as GymProfileCompleteness;

  applySearch(term: string): void {
    this.searchValue.set(term);
    const searchTerm = term.trim() || null;
    if (searchTerm === this.filters().searchTerm) {
      return;
    }
    this.filters.update(filters => ({ ...filters, searchTerm }));
    this.resetPage();
  }

  clearSearch(): void {
    this.applySearch('');
  }

  applyNetworks(networks: readonly NetworkFilter[]): void {
    this.filters.update(filters => ({ ...filters, networks }));
    this.resetPage();
  }

  sortBy(sorting: SortKey): void {
    if (sorting === this.filters().sorting) {
      return;
    }
    this.filters.update(filters => ({ ...filters, sorting }));
    this.resetPage();
  }

  onPage(event: PageEvent): void {
    this.pagination.set({ page: event.pageIndex, pageSize: event.pageSize });
  }

  dismissSummary(): void {
    this.summaryDismissed.set(true);
  }

  exportCsv(): void {
    if (this.exporting() || this.total() === 0) {
      return;
    }
    this.exporting.set(true);
    const { searchTerm, networks, sorting } = this.filters();
    this.service
      .getAll({
        sorting,
        ...(searchTerm && { searchTerm }),
        ...(networks.length && { networkFilter: networks }),
      })
      .pipe(
        finalize(() => this.exporting.set(false)),
        catchError(() => {
          this.snackBar.open(
            'Error exporting profile completeness data',
            'Close',
            { duration: 5000 }
          );
          return EMPTY;
        })
      )
      .subscribe(gyms => {
        const csvContent = this.csvService.convertToCSV(
          PROFILE_COMPLETENESS_CSV_MAPPINGS,
          gyms
        );
        this.downloadService.download(
          csvContent,
          'gym-profile-completeness.csv',
          'text/csv;charset=utf-8;'
        );
      });
  }

  private resetPage(): void {
    this.pagination.update(pagination => ({ ...pagination, page: 0 }));
  }
}

function toLoadState<T>() {
  return (source: Observable<T>): Observable<LoadState<T>> =>
    source.pipe(
      map(data => ({ status: 'loaded', data }) as LoadState<T>),
      startWith(LOADING as LoadState<T>),
      catchError((error: HttpErrorResponse) =>
        of({ status: 'error', error } as LoadState<T>)
      )
    );
}

function sameNetworks(
  a: readonly NetworkFilter[],
  b: readonly NetworkFilter[]
): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}
